import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { Globe2, FileText, SlidersHorizontal, LogOut, Radio, ScanLine, Archive, Truck, ScrollText } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import RegionSwitcher from "@/components/layout/RegionSwitcher";
import ConfirmDialog from "@/components/common/ConfirmDialog";

const NAV = [
  { to: "/", label: "Network map", icon: Globe2, testid: "nav-overview" },
  { to: "/register-device", label: "Register device", icon: ScanLine, testid: "nav-register" },
  { to: "/reports", label: "Reports", icon: FileText, testid: "nav-reports" },
  { to: "/history", label: "History", icon: Archive, testid: "nav-history" },
  { to: "/orders", label: "Track order", icon: Truck, testid: "nav-orders" },
  { to: "/logs", label: "Logs", icon: ScrollText, testid: "nav-logs" },
  { to: "/settings", label: "Catalog & settings", icon: SlidersHorizontal, testid: "nav-settings" },
];

function Clock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(t); }, []);
  return <span className="font-mono text-xs text-kl-muted" data-testid="topbar-clock">{now.toISOString().slice(11, 19)} UTC</span>;
}

export default function Shell() {
  const { user, logout } = useAuth();
  const { pathname } = useLocation();
  const [stats, setStats] = useState(null);
  const [confirmLogout, setConfirmLogout] = useState(false);

  useEffect(() => {
    let alive = true;
    const load = () => api.get("/stats").then((r) => alive && setStats(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 30000);
    return () => { alive = false; clearInterval(t); };
  }, [pathname]);

  return (
    <div className="h-full flex bg-kl-black text-white" data-testid="app-shell">
      <aside className="w-[232px] shrink-0 border-r border-kl-line flex flex-col">
        <div className="h-14 flex flex-col justify-center px-6 border-b border-kl-line">
          <span className="font-display font-semibold tracking-[0.35em] text-sm leading-none">KYRALINK</span>
          {user?.tenant_name && <span className="kl-label-dark mt-1 truncate" data-testid="shell-tenant-name">{user.tenant_name}</span>}
        </div>
        <nav className="flex-1 py-6">
          {NAV.map(({ to, label, icon: Icon, testid }) => (
            <NavLink key={to} to={to} end={to === "/"} data-testid={testid}
              className={({ isActive }) => `group flex items-center gap-3 px-6 h-11 text-sm border-l-2 transition-colors duration-150 ${isActive ? "border-kl-green text-white bg-kl-surface" : "border-transparent text-kl-muted hover:text-white hover:bg-kl-surface"}`}>
              <Icon size={16} strokeWidth={1.75} />
              <span className="font-body">{label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-kl-line p-5 space-y-3">
          <div className="grid grid-cols-2 gap-px bg-kl-line border border-kl-line">
            <div className="bg-kl-black p-3"><div className="kl-label-dark">Active</div><div className="font-mono text-lg text-kl-active" data-testid="sidebar-active-count">{stats?.active ?? "–"}</div></div>
            <div className="bg-kl-black p-3"><div className="kl-label-dark">Offline</div><div className="font-mono text-lg text-kl-inactive" data-testid="sidebar-inactive-count">{stats?.inactive ?? "–"}</div></div>
          </div>
          <div className="flex items-center justify-between pt-2">
            <div className="min-w-0">
              <div className="text-sm truncate" data-testid="user-name">{user?.name}</div>
              <div className="text-xs text-kl-muted truncate">{user?.email}</div>
            </div>
            <button onClick={() => setConfirmLogout(true)} data-testid="logout-button" title="Sign out" className="p-2 text-kl-muted hover:text-white hover:bg-kl-surface transition-colors duration-150 kl-focus"><LogOut size={16} /></button>
          </div>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-14 shrink-0 border-b border-kl-line flex items-center justify-between px-6">
          <div className="flex items-center gap-3">
            <Radio size={14} className="text-kl-green" />
            <span className="kl-label-dark">sys.terminal</span>
            <span className="text-kl-line">/</span>
            <span className="font-mono text-xs text-kl-muted hidden 2xl:inline">{stats ? `${stats.devices} nodes · ${stats.live_kw} kW live · ${stats.today_kwh} kWh today` : "syncing…"}</span>
          </div>
          <div className="flex items-center gap-6">
            <RegionSwitcher />
            <Clock />
          </div>
        </header>
        <main className="flex-1 min-h-0 overflow-hidden">
          <Outlet context={{ stats }} />
        </main>
      </div>

      <ConfirmDialog
        open={confirmLogout}
        onOpenChange={setConfirmLogout}
        title="Sign out?"
        description="You'll need to log back in to access the dashboard."
        confirmLabel="Sign out"
        onConfirm={() => { setConfirmLogout(false); logout(); }}
      />
    </div>
  );
}
